/**
 * @deprecated Use AI SDK 6 integration from './ai-sdk' instead.
 * This file is kept for backwards compatibility with existing code.
 */

import { type GPTChatModel, type GPTChatMessage, type GPTChatResult, type GPTChatMessageContentText, type GPTChatMessageContentImage, type OpenAiChatRequestHandlerExtra } from './openai';
import { VERCEL_URL, NODE_ENV, getModelConfig, isModelReasoning } from '../constant';

// Type definitions for Anthropic API (avoid direct SDK import issues)
type AnthropicMessageRole = 'user' | 'assistant';
type AnthropicMediaType = 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';

interface AnthropicTextBlockParam {
    type: 'text';
    text: string;
}

interface AnthropicImageBlockParam {
    type: 'image';
    source: {
        type: 'base64';
        media_type: AnthropicMediaType;
        data: string;
    };
}

type AnthropicContentBlock = AnthropicTextBlockParam | AnthropicImageBlockParam;

interface AnthropicMessageParam {
    role: AnthropicMessageRole;
    content: string | AnthropicContentBlock[];
}

const domain = VERCEL_URL || '';
const env = NODE_ENV || '';

// =============================================================================
// ANTHROPIC CONSTANTS
// =============================================================================

// Claude 4 and Claude 3.7 support extended thinking
const THINKING_CAPABLE_MODELS = [
    'claude-opus-4-20250514',
    'claude-sonnet-4-20250514',
    'claude-3-7-sonnet-latest',
];

// Default max tokens by model tier
const DEFAULT_MAX_TOKENS: Record<string, number> = {
    'claude-opus-4': 32000,
    'claude-sonnet-4': 16000,
    'claude-3-7': 16000,
    'claude-3-5': 8192,
    'claude-3': 4096,
};

const getMaxTokensForModel = (model: string): number => {
    for (const [prefix, tokens] of Object.entries(DEFAULT_MAX_TOKENS)) {
        if (model.includes(prefix)) return tokens;
    }
    return 8192; // Default
};

// =============================================================================
// MESSAGE CONVERSION
// =============================================================================

export const convertGptMessagesContentsToString = (
    content: string | (GPTChatMessageContentText | GPTChatMessageContentImage)[]
): string => {
    if (typeof content === 'string') return content;
    return content?.map((c: GPTChatMessageContentText | GPTChatMessageContentImage) => {
        if (c.type === 'image_url') {
            return c.image_url?.url;
        }
        return c.text;
    }).join("\n\n");
};

export const convertGptMessagesToAnthropicMessages = (
    messages: GPTChatMessage[]
): { messages: Array<AnthropicMessageParam>; system: string } => {
    let systemMessage = '';

    const antMessages = messages
        .map((message): AnthropicMessageParam | null => {
            if (message.role === 'system') {
                systemMessage = convertGptMessagesContentsToString(message.content);
                return null;
            }

            // Handle images for Claude's vision capability
            if (Array.isArray(message.content)) {
                const parts: Array<AnthropicTextBlockParam | AnthropicImageBlockParam> = [];

                for (const part of message.content) {
                    if (part.type === 'text') {
                        parts.push({ type: 'text', text: part.text });
                    } else if (part.type === 'image_url') {
                        // Claude expects base64 images or URLs
                        const imageUrl = part.image_url.url;
                        if (imageUrl.startsWith('data:')) {
                            // Base64 image
                            const matches = imageUrl.match(/^data:(.+);base64,(.+)$/);
                            if (matches) {
                                parts.push({
                                    type: 'image',
                                    source: {
                                        type: 'base64',
                                        media_type: matches[1] as AnthropicMediaType,
                                        data: matches[2]!,
                                    },
                                });
                            }
                        } else {
                            // URL image - Claude uses base64, so we'd need to fetch and convert
                            // For now, skip URL images that aren't base64
                            console.warn('Claude requires base64 images, skipping URL:', imageUrl);
                        }
                    }
                }

                return {
                    role: message.role === 'assistant' ? 'assistant' : 'user',
                    content: parts,
                };
            }

            return {
                role: message.role === 'assistant' ? 'assistant' : 'user',
                content: convertGptMessagesContentsToString(message.content),
            };
        })
        .filter((message): message is AnthropicMessageParam => message !== null);

    return {
        messages: antMessages,
        system: systemMessage,
    };
};

// =============================================================================
// ANTHROPIC HANDLER (DEPRECATED)
// =============================================================================

export interface AnthropicHandlerExtra extends OpenAiChatRequestHandlerExtra {
    enableThinking?: boolean;
    thinkingBudgetTokens?: number;
}

/**
 * @deprecated Use `aiSDKChatHandler` from './ai-sdk' instead.
 * This handler is kept for backwards compatibility but will be removed in a future version.
 * 
 * The AI SDK 6 provides a unified interface for all providers including Anthropic.
 * 
 * @example
 * // New way (recommended):
 * import { aiSDKChatHandler } from './ai-sdk';
 * const result = await aiSDKChatHandler(apiKeys, 'claude-sonnet-4-5', messages);
 */
export const anthropicChatRequestHandler = async <T extends AnthropicHandlerExtra | undefined>(
    apiKey: string,
    model: GPTChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    extra?: T
): Promise<T extends { useStreaming: true } ? null : GPTChatResult> => {
    // This function is deprecated - redirect users to AI SDK
    throw new Error(
        'anthropicChatRequestHandler is deprecated. ' +
        'Please use aiSDKChatHandler from @acme/util instead. ' +
        'Example: aiSDKChatHandler(apiKeys, "claude-sonnet-4-5", messages)'
    );
};
